import { useEffect, useRef } from 'react';
import { useWalkthrough } from '../useWalkthrough';
import { TASK_MANAGEMENT_PAGE, TASK_MANAGEMENT_WALKTHROUGH_ID } from '../registry';

/** `currentPage` value that holds the task tour (nothing shown, nothing skipped). */
export const TASK_MANAGEMENT_HELD_PAGE = 'task-away';

/**
 * Task page side of the task-management tour (Figma 9607-20688).
 *
 * The `WalkthroughRunner` the page mounts does the gating: `never-seen`
 * auto-start once per user when the task has evaluation results, resume of
 * an in-progress one, replay from the header's "View Guided Tour" button.
 * This hook adds what the runner cannot know:
 *
 * - `onStart` fires each time the tour becomes the active walkthrough, so
 *   the page can switch to the Evaluations tab the marks live on — whether
 *   the tour auto-started, resumed after a reload, or was replayed;
 * - `currentPage` is the runner's page: the tour's one page while the
 *   Evaluations tab is showing, and a page outside the tour's `pageOrder`
 *   while the Playground overlay is open or another tab is active. The
 *   pointer then waits — the marks reappear where they were once the user
 *   is back — instead of pointing at controls that are not on screen.
 *
 * Returns `{ running, currentPage }`.
 */
export function useTaskManagementTour({ onStart, isEvaluationTabActive, isPlaygroundOpen = false }) {
    const { active } = useWalkthrough();
    const running = active === TASK_MANAGEMENT_WALKTHROUGH_ID;

    const onStartRef = useRef(onStart);
    onStartRef.current = onStart;
    const wasRunningRef = useRef(false);
    useEffect(() => {
        if (running && !wasRunningRef.current) onStartRef.current?.();
        wasRunningRef.current = running;
    }, [running]);

    const currentPage = isEvaluationTabActive && !isPlaygroundOpen ? TASK_MANAGEMENT_PAGE : TASK_MANAGEMENT_HELD_PAGE;
    return { running, currentPage };
}

export default useTaskManagementTour;
